import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth';
import { useToast } from '@/hooks/use-toast';
import { errorMessage, toError } from '@/lib/errors';
import { ArrowLeft, Loader2, Eye, EyeOff } from 'lucide-react';
import { z } from 'zod';
import { Hero3DOverlay } from '@/components/landing/HeroBackground3D';
import { Logo } from '@/components/ui/Logo';
import { PublicFooter } from '@/components/layout/PublicFooter';
import { TrialConfirmationModal } from '@/components/trial/TrialConfirmationModal';
import { supabase } from '@/integrations/supabase/client';
import { absoluteUrl } from '@/lib/navigation';
import { isInviteRedirect } from '@/lib/inviteRedirect';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

const signupSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100),
  email: z.string().email('Please enter a valid email').max(255),
  password: z.string().min(6, 'Password must be at least 6 characters').max(72),
  workspaceName: z.string().min(2, 'Company name must be at least 2 characters').max(100),
});

export default function Auth() {
  const [searchParams] = useSearchParams();
  const isSignup = searchParams.get('mode') === 'signup';
  const navigate = useNavigate();
  const { user, signIn, signUp, signUpInviteOnly, loading: authLoading } = useAuth();
  const { toast } = useToast();

  const redirectParam = searchParams.get('redirect');
  const isInviteFlow = isInviteRedirect(redirectParam ?? null);

  const [mode, setMode] = useState<'login' | 'signup'>(isSignup ? 'signup' : 'login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showTrialConfirmation, setShowTrialConfirmation] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');

  // Handle checkout status toasts and error query params
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      toast({
        variant: 'destructive',
        title: 'Authentication error',
        description: decodeURIComponent(errorParam),
      });
      // Clean up the error from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('error');
      navigate(`/auth?${newSearchParams.toString()}`, { replace: true });
    }
  }, [searchParams, toast, navigate]);

  // Redirect if already authenticated
  useEffect(() => {
    if (user && !authLoading) {
      const redirectParam = searchParams.get('redirect');
      if (redirectParam) {
        navigate(decodeURIComponent(redirectParam));
      } else {
        navigate('/app');
      }
    }
  }, [user, authLoading, navigate, searchParams]);

  useEffect(() => {
    setMode(isSignup ? 'signup' : 'login');
  }, [isSignup]);

  const validateForm = () => {
    setErrors({});
    
    try {
      if (mode === 'login') {
        loginSchema.parse({ email, password });
      } else if (isInviteFlow) {
        signupSchema.omit({ workspaceName: true }).parse({ name, email, password });
      } else {
        signupSchema.parse({ name, email, password, workspaceName });
      }
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    if (mode === 'signup') {
      if (isInviteFlow) {
        await performSignupInvite();
        return;
      }
      setShowTrialConfirmation(true);
      return;
    }

    await performLogin();
  };

  const performLogin = async () => {
    setLoading(true);
    try {
      const { error } = await signIn(email, password);
      if (error) {
        // Log full error in dev mode for debugging
        if (import.meta.env.DEV) {
          console.error('[Auth] Login error:', {
            message: error.message,
            status: (error as any).status,
            code: (error as any).code,
          });
        }
        
        // Provide specific error messages based on error type
        if (error.message.includes('Invalid login credentials')) {
          toast({
            variant: 'destructive',
            title: 'Invalid credentials',
            description: 'Email or password incorrect, or user does not exist in this Supabase project.',
          });
        } else if (error.message.includes('Email not confirmed')) {
          toast({
            variant: 'destructive',
            title: 'Email not confirmed',
            description: 'Please check your inbox and confirm your email, or request a new confirmation link.',
          });
        } else {
          throw toError(error, 'Login failed');
        }
      } else {
        const redirectParam = searchParams.get('redirect');
        if (redirectParam) {
          navigate(decodeURIComponent(redirectParam));
        } else {
          navigate('/app');
        }
      }
    } catch (error: any) {
      // Log full error in dev mode
      if (import.meta.env.DEV) {
        console.error('[Auth] Unexpected login error:', error);
      }
      toast({
        variant: 'destructive',
        title: 'Error',
        description: errorMessage(error, 'Something went wrong. Please try again.'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast({
        variant: 'destructive',
        title: 'Email required',
        description: 'Please enter your email address first.',
      });
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: absoluteUrl('/reset-password'),
      });
      
      if (error) throw toError(error, 'Failed to send password reset email');
      
      toast({
        title: 'Password reset email sent',
        description: 'Check your inbox for the password reset link.',
      });
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('[Auth] Password reset error:', error);
      }
      toast({
        variant: 'destructive',
        title: 'Error',
        description: errorMessage(error, 'Failed to send password reset email.'),
      });
    }
  };

  const performSignupInvite = async () => {
    setLoading(true);
    try {
      const { error } = await signUpInviteOnly(email, password, name);
      if (error) {
        if (error.message.includes('already registered')) {
          toast({
            variant: 'destructive',
            title: 'Account exists',
            description: 'An account with this email already exists. Please sign in instead.',
          });
        } else {
          throw toError(error, 'Signup failed');
        }
      } else {
        if (redirectParam) navigate(decodeURIComponent(redirectParam));
        else navigate('/app');
      }
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: errorMessage(error, 'Something went wrong. Please try again.'),
      });
    } finally {
      setLoading(false);
    }
  };

  const performSignup = async () => {
    setLoading(true);
    try {
      const { error } = await signUp(email, password, name, workspaceName, true);
      if (error) {
        if (error.message.includes('already registered')) {
          toast({
            variant: 'destructive',
            title: 'Account exists',
            description: 'An account with this email already exists. Please login instead.',
          });
        } else {
          throw toError(error, 'Signup failed');
        }
      } else {
        toast({
          title: 'Welcome to Decision Sales OS!',
          description: 'Your 14-day free trial has started. Let\'s get going!',
        });
        if (redirectParam) {
          navigate(decodeURIComponent(redirectParam));
        } else {
          navigate('/app');
        }
      }
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: errorMessage(error, 'Something went wrong. Please try again.'),
      });
    } finally {
      setLoading(false);
      setShowTrialConfirmation(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Trial Confirmation Modal */}
      <TrialConfirmationModal
        open={showTrialConfirmation}
        onOpenChange={setShowTrialConfirmation}
        onConfirm={performSignup}
        loading={loading}
      />

      {/* Main content: left form + right panel */}
      <div className="flex-1 flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-8 lg:px-16 xl:px-24">
        <div className="max-w-md w-full mx-auto">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          {/* Logo */}
          <div className="mb-8">
            <Logo size="lg" />
          </div>

          {/* Auth Card */}
          <div className="bg-card border border-border/50 rounded-2xl p-8 shadow-lg">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold mb-2">
                {isInviteFlow
                  ? (mode === 'signup' ? 'Create account' : 'Sign in to accept your invitation')
                  : (mode === 'signup' ? 'Start your free trial' : 'Welcome back')}
              </h1>
              <p className="text-muted-foreground">
                {isInviteFlow
                  ? (mode === 'signup' ? 'Join the workspace you were invited to.' : 'Sign in to accept your invitation.')
                  : (mode === 'signup' ? '14 days free trial.' : 'Sign in to access your decision dashboard.')}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'signup' && (
                <>
                  {!isInviteFlow && (
                    <div className="space-y-2">
                      <Label htmlFor="workspaceName">Company Name</Label>
                      <Input
                        id="workspaceName"
                        type="text"
                        placeholder="Acme Inc."
                        value={workspaceName}
                        onChange={(e) => setWorkspaceName(e.target.value)}
                        className={errors.workspaceName ? 'border-destructive' : ''}
                      />
                      {errors.workspaceName && (
                        <p className="text-sm text-destructive">{errors.workspaceName}</p>
                      )}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="name">Your Name</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={errors.name ? 'border-destructive' : ''}
                    />
                    {errors.name && (
                      <p className="text-sm text-destructive">{errors.name}</p>
                    )}
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={errors.email ? 'border-destructive' : ''}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={errors.password ? 'border-destructive pr-10' : 'pr-10'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password}</p>
                )}
                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="text-sm text-primary hover:underline"
                  >
                    Forgot password?
                  </button>
                )}
              </div>

              <Button 
                type="submit" 
                variant="hero" 
                className="w-full" 
                size="lg"
                disabled={loading}
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {mode === 'signup' ? (isInviteFlow ? 'Create account' : 'Start 14-day free trial') : 'Sign in'}
              </Button>
            </form>

            {/* Toggle Mode */}
            <div className="mt-6 text-center text-sm">
              {mode === 'signup' ? (
                <p className="text-muted-foreground">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setMode('login')}
                    className="text-primary hover:underline font-medium"
                  >
                    Sign in
                  </button>
                </p>
              ) : (
                <p className="text-muted-foreground">
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setMode('signup')}
                    className="text-primary hover:underline font-medium"
                  >
                    {isInviteFlow ? 'Sign up' : 'Start free trial'}
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Animation */}
      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-[#0B0D10]" />
        
        {/* 3D Animation overlay */}
        <Hero3DOverlay scrollY={0} />
        
        {/* Content overlay */}
        <div className="relative z-10 flex flex-col items-center justify-center p-12 text-center">
          <h2 className="text-3xl font-semibold text-white mb-4">
            Make smarter sales decisions.
          </h2>
          <p className="text-white/70 text-lg max-w-sm">
            Daily priorities. Clear actions. Less noise.
          </p>
        </div>
      </div>
      </div>
      <PublicFooter />
    </div>
  );
}
